export type LoadBand = 'available' | 'healthy' | 'high' | 'overloaded';

export type ResourceWorkItem = {
  id: string;
  project_id: string;
  project_name: string;
  key: string;
  title: string;
  state_id: string;
  state_name: string;
  state_group: string;
  priority: string;
  type: string;
  estimate_hours: number | null;
  allocated_hours: number;
  blocked: boolean;
};

export type ClosedWeek = {
  week_start: string;
  hours: number;
  item_count: number;
};

export type ResourceRow = {
  user_id: string;
  first_name: string | null;
  last_name: string | null;
  email: string;
  avatar_url?: string | null;
  weekly_capacity_hours: number;
  allocated_hours: number;
  available_hours: number;
  load_percent: number;
  load_band: LoadBand;
  unestimated_count: number;
  active_count: number;
  current: ResourceWorkItem | null;
  next: ResourceWorkItem | null;
  items: ResourceWorkItem[];
  buckets: Array<{ key: 'engineering' | 'incidents'; hours: number }>;
  closed_weeks: ClosedWeek[];
};

export type ResourcesOverview = {
  period: 'this_week';
  summary: {
    resource_count: number;
    loaded_percent: number;
    overloaded_count: number;
    available_count: number;
    allocated_hours: number;
    capacity_hours: number;
  };
  resources: ResourceRow[];
};

export type ResourceLoad = {
  user_id: string;
  load_percent: number;
  load_band: LoadBand;
  available_hours: number;
  allocated_hours: number;
  weekly_capacity_hours: number;
};

export function resourceDisplayName(row: Pick<ResourceRow, 'first_name' | 'last_name' | 'email'>): string {
  const name = [row.first_name, row.last_name]
    .filter((part): part is string => typeof part === 'string' && part.trim().length > 0)
    .join(' ')
    .trim();
  return name || row.email;
}

export function loadBand(percent: number): LoadBand {
  if (percent > 100) return 'overloaded';
  if (percent >= 85) return 'high';
  if (percent >= 60) return 'healthy';
  return 'available';
}

export type CapacityRag = 'green' | 'amber' | 'red';

/** Green = leftover room, amber = 60%+ used, red = full or over. */
export function capacityRag(usedHours: number, capacityHours: number): CapacityRag {
  if (typeof usedHours !== 'number' || !Number.isFinite(usedHours) || usedHours < 0) {
    throw new Error('Used hours must be a finite number of 0 or more.');
  }
  if (typeof capacityHours !== 'number' || !Number.isFinite(capacityHours) || capacityHours <= 0) {
    throw new Error('Capacity hours must be a positive number.');
  }
  if (usedHours >= capacityHours) return 'red';
  if ((usedHours / capacityHours) * 100 >= 60) return 'amber';
  return 'green';
}

export function capacityRagLabel(rag: CapacityRag): string {
  if (rag === 'green') return 'Free';
  if (rag === 'amber') return 'Tight';
  return 'Full';
}

export function loadBandLabel(band: LoadBand): string {
  if (band === 'overloaded') return 'Overloaded';
  if (band === 'high') return 'High';
  if (band === 'healthy') return 'Healthy';
  return 'Available';
}
